// src/pages/LegalPage.js
//
// Une seule page pour les deux documents légaux.
// Routes attendues :
//   <Route path="/mentions-legales" element={<LegalPage doc="mentions" />} />
//   <Route path="/cgu"             element={<LegalPage doc="cgu" />} />
//
// ⚠️ Les mentions marquées « À COMPLÉTER » doivent être renseignées avant
//    mise en production : la loi impose ces informations pour un service en ligne.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { ArrowLeft, ArrowUpRight, FileText, Scale } from 'lucide-react';
import styles from './LegalPage.module.css';
import logo from '../assets/images/zone_icon.png';

/* ═══════════════════════════════════════════════════════════
   CONTENU — MENTIONS LÉGALES
   ═══════════════════════════════════════════════════════════ */
const MENTIONS = {
  key: 'mentions',
  Icon: Scale,
  eyebrow: 'Informations légales',
  title: 'Mentions légales',
  intro:
    "Informations relatives à l’éditeur, à l’hébergeur et aux conditions de publication du service CVtoSkill, conformément à la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l’économie numérique.",
  updatedAt: 'Septembre 2026',
  sections: [
    {
      id: 'editeur',
      title: 'Éditeur du site',
      blocks: [
        { type: 'definitions', items: [
          { term: 'Dénomination sociale', value: 'CVtoSkill' },
          { term: 'Forme juridique',      value: 'Société par actions simplifiée (SAS)' },
          { term: 'Capital social',       value: '[À COMPLÉTER]' },
          { term: 'Siège social',         value: '[Adresse complète — À COMPLÉTER]' },
          { term: 'SIREN / SIRET',        value: '[À COMPLÉTER]' },
          { term: 'TVA intracommunautaire', value: '[À COMPLÉTER]' },
          { term: 'Téléphone',            value: '+33 6 12 01 81 41' },
        ]},
      ],
    },
    {
      id: 'publication',
      title: 'Directeur de la publication',
      blocks: [
        { type: 'definitions', items: [
          { term: 'Nom',      value: '[Nom du représentant légal — À COMPLÉTER]' },
          { term: 'Qualité',  value: 'Président de CVtoSkill' },
          { term: 'Contact',  value: '+33 6 12 01 81 41' },
        ]},
      ],
    },
    {
      id: 'hebergeur',
      title: 'Hébergeur',
      blocks: [
        { type: 'definitions', items: [
          { term: 'Prestataire',  value: 'Microsoft Azure — Microsoft Corporation' },
          { term: 'Siège social', value: 'One Microsoft Way, Redmond, WA 98052-6399, États-Unis' },
          { term: 'Site',         value: 'https://azure.microsoft.com' },
        ]},
        { type: 'p', text:
          "Les traitements et le stockage des documents déposés sur la plateforme sont réalisés sur l’infrastructure Azure. Les transferts éventuels hors Union européenne sont encadrés par les clauses contractuelles types de la Commission européenne." },
      ],
    },
    {
      id: 'propriete',
      title: 'Propriété intellectuelle',
      blocks: [
        { type: 'p', text:
          "La structure du site, son interface, ses textes, ses éléments graphiques et le code source de la plateforme sont la propriété exclusive de CVtoSkill. Toute reproduction, représentation ou adaptation, totale ou partielle, sans autorisation écrite préalable est interdite." },
        { type: 'p', text:
          "L’utilisateur conserve l’intégralité des droits sur les documents qu’il dépose ainsi que sur les livrables générés à partir de ceux-ci. CVtoSkill ne revendique aucun droit sur ces contenus." },
      ],
    },
    {
      id: 'contact-legal',
      title: 'Nous contacter',
      blocks: [
        { type: 'p', text:
          "Pour toute question relative aux présentes mentions, à l’exercice de vos droits sur vos données ou au signalement d’un contenu, vous pouvez nous joindre au +33 6 12 01 81 41, ou depuis le formulaire de contact accessible sur la page d’accueil." },
      ],
    },
  ],
};

/* ═══════════════════════════════════════════════════════════
   CONTENU — CONDITIONS GÉNÉRALES D'UTILISATION
   ═══════════════════════════════════════════════════════════ */
const CGU = {
  key: 'cgu',
  Icon: FileText,
  eyebrow: 'Conditions',
  title: 'Conditions générales d’utilisation',
  intro:
    "Les présentes conditions régissent l’accès et l’utilisation de la plateforme CVtoSkill et de ses modules. La création d’un compte vaut acceptation sans réserve de ces conditions.",
  updatedAt: 'Septembre 2026',
  sections: [
    {
      id: 'objet',
      title: 'Article 1 — Objet',
      blocks: [
        { type: 'p', text:
          "CVtoSkill met à disposition une plateforme en ligne d’assistance à la production et à la qualification de documents RH, composée de modules indépendants : génération de dossiers de compétences, rédaction de fiches de poste, audit de CV, matching entre un profil et une offre, et recherche d’offres." },
        { type: 'p', text:
          "Les présentes conditions s’appliquent à tout utilisateur disposant d’un accès, quel que soit son niveau de droits." },
      ],
    },
    {
      id: 'acces',
      title: 'Article 2 — Accès au service',
      blocks: [
        { type: 'p', text:
          "Le service est accessible en continu, sous réserve des opérations de maintenance et des incidents indépendants de notre volonté. L’accès requiert un compte nominatif, dont les identifiants sont strictement personnels et placés sous la responsabilité de leur titulaire." },
        { type: 'list', items: [
          "Les modules sont activés compte par compte : un utilisateur n’accède qu’aux modules qui lui ont été ouverts.",
          "Les administrateurs d’une organisation peuvent créer, suspendre et supprimer les comptes de leurs collaborateurs.",
          "Certains traitements consomment des crédits, décomptés au lancement de l’analyse.",
        ]},
      ],
    },
    {
      id: 'usage',
      title: 'Article 3 — Conditions d’usage',
      blocks: [
        { type: 'p', text:
          "L’utilisateur s’engage à ne déposer que des documents qu’il est en droit de traiter, et à avoir recueilli, lorsque la loi l’exige, le consentement ou l’information préalable des personnes concernées — notamment des candidats dont il dépose le CV." },
        { type: 'list', items: [
          "Aucun usage détourné, automatisé abusif ou visant à contourner les limites du service.",
          "Aucun dépôt de contenu illicite, diffamatoire ou portant atteinte aux droits de tiers.",
          "Aucune tentative d’accès à des données appartenant à une autre organisation.",
        ]},
      ],
    },
    {
      id: 'responsabilite',
      title: 'Article 4 — Responsabilité',
      blocks: [
        { type: 'p', text:
          "CVtoSkill fournit une aide à la décision et à la production documentaire. Les résultats produits par les modèles d’intelligence artificielle — scores, synthèses, recommandations, documents générés — peuvent comporter des erreurs, des omissions ou des approximations." },
        { type: 'callout', text:
          "La validation finale des documents et des décisions de recrutement relève entièrement de l’utilisateur. Aucune décision produisant des effets juridiques à l’égard d’une personne ne doit être prise sur le seul fondement d’un résultat automatisé." },
        { type: 'p', text:
          "CVtoSkill ne saurait être tenue responsable des conséquences d’une utilisation non conforme du service, ni des dommages indirects tels qu’une perte d’exploitation ou de chiffre d’affaires." },
      ],
    },
    {
      id: 'donnees',
      title: 'Article 5 — Données personnelles et confidentialité',
      blocks: [
        { type: 'p', text:
          "Dans le cadre du service, CVtoSkill agit en qualité de sous-traitant au sens du RGPD : l’utilisateur, ou son organisation, reste responsable de traitement des données qu’il dépose." },
        { type: 'list', items: [
          "Les documents déposés sont traités dans le seul but d’exécuter le module demandé.",
          "Ils ne sont jamais utilisés pour entraîner nos modèles ni ceux de tiers, et ne sont ni revendus ni cédés.",
          "Les échanges sont chiffrés en transit, et chaque module s’exécute dans un environnement isolé.",
          "Les documents et résultats sont conservés le temps nécessaire à leur restitution et à la tenue de votre historique, puis supprimés sur demande.",
        ]},
        { type: 'p', text:
          "Conformément au règlement (UE) 2016/679, toute personne dispose d’un droit d’accès, de rectification, d’effacement, de limitation et d’opposition sur les données la concernant. Ces demandes s’exercent auprès de l’organisation ayant déposé le document ; CVtoSkill lui apporte son concours pour y répondre. Vous pouvez nous écrire au +33 6 12 01 81 41 ou via le formulaire de contact." },
        { type: 'p', text:
          "En cas de désaccord persistant, une réclamation peut être adressée à la Commission nationale de l’informatique et des libertés (CNIL), 3 place de Fontenoy, 75007 Paris." },
      ],
    },
    {
      id: 'propriete-cgu',
      title: 'Article 6 — Propriété intellectuelle',
      blocks: [
        { type: 'p', text:
          "La plateforme, son architecture modulaire et ses interfaces demeurent la propriété de CVtoSkill. L’utilisateur conserve la propriété pleine et entière de ses documents d’entrée comme des livrables générés, et peut en faire un usage commercial libre." },
      ],
    },
    {
      id: 'evolution',
      title: 'Article 7 — Évolution des conditions',
      blocks: [
        { type: 'p', text:
          "CVtoSkill peut modifier les présentes conditions, notamment à l’occasion de l’ajout de nouveaux modules ou d’évolutions réglementaires. Les utilisateurs sont informés de toute modification substantielle, la version applicable étant celle publiée sur cette page à la date d’utilisation." },
      ],
    },
    {
      id: 'droit',
      title: 'Article 8 — Droit applicable et juridiction',
      blocks: [
        { type: 'p', text:
          "Les présentes conditions sont régies par le droit français. À défaut de résolution amiable, tout litige relève de la compétence exclusive des tribunaux de Paris." },
      ],
    },
  ],
};

const DOCS = { mentions: MENTIONS, cgu: CGU };

/* ═══════════════════════════════════════════════════════════
   RENDU D'UN BLOC
   ═══════════════════════════════════════════════════════════ */
const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <p className={styles.paragraph}>{block.text}</p>;

    case 'list':
      return (
        <ul className={styles.list}>
          {block.items.map((item) => <li key={item}>{item}</li>)}
        </ul>
      );

    case 'definitions':
      return (
        <dl className={styles.definitions}>
          {block.items.map(({ term, value }) => (
            <div key={term} className={styles.definitionRow}>
              <dt>{term}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      );

    case 'callout':
      return <p className={styles.callout}>{block.text}</p>;

    default:
      return null;
  }
};

/* ═══════════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL
   ═══════════════════════════════════════════════════════════ */
const LegalPage = ({ doc = 'mentions' }) => {
  const { hash, pathname } = useLocation();
  const content = useMemo(() => DOCS[doc] ?? MENTIONS, [doc]);
  const [activeId, setActiveId] = useState(content.sections[0]?.id);

  // Remonte en haut au changement de document, ou saute à l'ancre demandée
  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname, hash]);

  // Met en surbrillance la section en cours de lecture
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveId(visible.target.id);
      },
      { rootMargin: '-20% 0px -65% 0px', threshold: 0 }
    );

    content.sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [content]);

  const { Icon } = content;

  return (
    <div className={styles.page}>

      {/* ══ Barre supérieure ══ */}
      <header className={styles.topBar}>
        <div className={styles.topBarInner}>
          <Link to="/" className={styles.brand}>
            <img src={logo} alt="" className={styles.brandLogo} />
            <span className={styles.brandName}>CVtoSkill</span>
          </Link>

          <Link to="/" className={styles.backLink}>
            <ArrowLeft size={15} strokeWidth={2} />
            Retour à l’accueil
          </Link>
        </div>
      </header>

      {/* ══ En-tête du document ══ */}
      <section className={styles.hero}>
        <div className={styles.inner}>
          <span className={styles.eyebrow}>
            <Icon size={13} strokeWidth={2.25} />
            {content.eyebrow}
          </span>
          <h1 className={styles.title}>{content.title}</h1>
          <p className={styles.intro}>{content.intro}</p>
          <p className={styles.updated}>Dernière mise à jour : {content.updatedAt}</p>
        </div>
      </section>

      {/* ══ Corps ══ */}
      <div className={styles.inner}>
        <div className={styles.layout}>

          {/* Sommaire */}
          <aside className={styles.toc} aria-label="Sommaire">
            <p className={styles.tocTitle}>Sommaire</p>
            <nav className={styles.tocNav}>
              {content.sections.map(({ id, title }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`${styles.tocLink} ${activeId === id ? styles.tocLinkActive : ''}`}
                >
                  {title}
                </a>
              ))}
            </nav>

            <div className={styles.tocSwitch}>
              {content.key === 'cgu' ? (
                <Link to="/mentions-legales" className={styles.tocSwitchLink}>
                  Mentions légales
                  <ArrowUpRight size={14} strokeWidth={2} />
                </Link>
              ) : (
                <Link to="/cgu" className={styles.tocSwitchLink}>
                  Conditions d’utilisation
                  <ArrowUpRight size={14} strokeWidth={2} />
                </Link>
              )}
            </div>
          </aside>

          {/* Articles */}
          <main className={styles.content}>
            {content.sections.map(({ id, title, blocks }) => (
              <section key={id} id={id} className={styles.section}>
                <h2 className={styles.sectionTitle}>{title}</h2>
                {blocks.map((block, i) => (
                  <Block key={i} block={block} />
                ))}
              </section>
            ))}

            <div className={styles.footerNote}>
              <p>
                Une question sur ce document ? Écrivez-nous depuis la{' '}
                <HashLink to="/#contact">page de contact</HashLink> ou appelez le{' '}
                <a href="tel:+33612018141">+33 6 12 01 81 41</a>.
              </p>
            </div>
          </main>
        </div>
      </div>

      {/* ══ Pied de page ══ */}
      <footer className={styles.footer}>
        <div className={styles.inner}>
          <p className={styles.copyright}>
            © {new Date().getFullYear()} CVtoSkill — Tous droits réservés
          </p>
          <nav className={styles.footerLinks}>
            <Link to="/mentions-legales">Mentions légales</Link>
            <Link to="/cgu">CGU</Link>
            <Link to="/">Accueil</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;